import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import { filter, map, startWith } from 'rxjs';

import { FooterComponent } from './components/footer/footer.component';
import { TopbarComponent } from './components/topbar/topbar.component';
import { SessionExpiryService } from './services/session-expiry.service';

// Routes that suppress the chrome (header/footer) — currently just the
// access-denied page, which is reached without an authenticated session.
const SHELL_HIDDEN_ROUTES = ['/access-denied'];

// Auth0 SPA-SDK error codes that mean "the silent-renewal attempt requires
// the user to log in again." Anything else (network blips, generic errors)
// should not bounce the user through Auth0.
const SILENT_RENEWAL_FAILURE_CODES = new Set(['login_required', 'consent_required', 'interaction_required']);

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, TopbarComponent, FooterComponent],
  templateUrl: './app.html',
  styleUrl: './app.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class App implements OnInit {
  private readonly router = inject(Router);
  private readonly auth = inject(AuthService);
  private readonly sessionExpiry = inject(SessionExpiryService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map((e) => e.urlAfterRedirects),
      startWith(this.router.url),
    ),
    { initialValue: this.router.url },
  );

  readonly showShell = computed(() => {
    const url = this.currentUrl() || '/';
    return !SHELL_HIDDEN_ROUTES.some((r) => url === r || url.startsWith(`${r}?`));
  });

  ngOnInit(): void {
    // Silent-renewal failures (e.g. refresh token expired, Auth0 session
    // revoked) surface here. Bounce through Auth0 so the SDK can recover.
    this.auth.error$
      .pipe(
        filter((err) => SILENT_RENEWAL_FAILURE_CODES.has(getAuthErrorCode(err))),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.sessionExpiry.triggerReauth());

    // After the Auth0 redirect completes, the SDK emits whatever appState
    // we passed to loginWithRedirect. If a target URL was captured, return
    // the user to where they were when their session expired.
    this.auth.appState$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((appState) => {
        const target = (appState as { target?: string } | undefined)?.target;
        if (target) {
          this.router.navigateByUrl(target);
        }
      });

    // Once auth is healthy again, clear the redirect-in-flight flag so a
    // future expiry can re-trigger the flow.
    this.auth.isAuthenticated$
      .pipe(
        filter((authed) => authed),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.sessionExpiry.reset());
  }
}

function getAuthErrorCode(err: Error): string {
  // Auth0's SPA-SDK errors carry the OAuth error code on an `error` property,
  // which isn't on the base Error type — read defensively.
  const code = (err as { error?: unknown }).error;
  return typeof code === 'string' ? code : '';
}
